import React, { useMemo, useState } from 'react';
import { Dimmer, Loader } from 'semantic-ui-react';
import { ChainLinkIcon, SearchIcon, ArrowIcon } from '../icons';
import './style.less';

/* Layout-only Tailwind (spacing/flex structure); colors, gradients, shadows and
   animations stay in style.less scoped under .neighbors-links. nbp-* tokens are
   px-based so they don't shrink with the responsive root font-size. */
const LINKS_SHELL =
    'neighbors-links box-border relative mx-auto min-w-0 w-full max-w-links-page flex-1 px-nbp-16 pt-nbp-20 pb-nbp-32 sm:px-nbp-24 sm:pt-nbp-24 sm:pb-nbp-40 md:px-nbp-36 md:pt-nbp-36 md:pb-nbp-64';
const LINKS_HERO =
    'links-hero flex min-w-0 w-full flex-col flex-nowrap items-start gap-nbp-20 p-nbp-20 sm:p-nbp-24 md:px-nbp-40 md:py-nbp-36 lg:flex-row lg:items-center lg:justify-between lg:gap-nbp-24';
// flex-auto + a max-width cap (not a fixed width + flex-shrink-0) so the field shrinks
// gracefully instead of clipping/overflowing once the row layout kicks in at `lg`.
const LINKS_HERO_SEARCH =
    'links-hero-search relative z-10 min-w-0 w-full flex-auto lg:max-w-links-search';
const LINKS_SECTION_HEADER = 'links-section-header mb-nbp-16 flex min-w-0 flex-wrap items-center gap-nbp-12';
const LINKS_CARD =
    'link-card flex min-w-0 w-full items-center gap-nbp-12 p-nbp-16 sm:gap-nbp-16 sm:p-nbp-20';
const LINKS_EMPTY = 'links-empty-state min-w-0 w-full px-nbp-16 py-nbp-32 sm:px-nbp-20 sm:py-nbp-48';

export interface AppLink {
    label?: string;
    link?: string;
}

interface LinksListProps {
    links: AppLink[];
    loading: boolean;
}

const normalizeUrl = (url: string): string =>
    /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;

const hostnameOf = (url: string): string => {
    try {
        return new URL(normalizeUrl(url)).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

// Anything rendered in the grid/empty-state below must be derived from validLinks/visibleLinks
// (both filtered through this guard) so `item.link` can be safely used as a non-optional string.
const hasValidLink = (item: AppLink): item is AppLink & { link: string } =>
    typeof item.link === 'string' && item.link.trim().length > 0;

interface DisplayLink {
    key: string;
    label: string;
    href: string;
    hostname: string;
    hasLabel: boolean;
}

const toDisplayLink = (item: AppLink & { link: string }, index: number): DisplayLink => {
    const trimmedLabel = item.label?.trim();
    return {
        key: `${item.link}-${index}`,
        label: trimmedLabel || item.link.trim(),
        href: normalizeUrl(item.link),
        hostname: hostnameOf(item.link),
        hasLabel: Boolean(trimmedLabel),
    };
};


const LinksList = ({ links, loading }: LinksListProps) => {
    const [searchQuery, setSearchQuery] = useState('');

    const validLinks = useMemo(
        () => links.filter(hasValidLink).map(toDisplayLink),
        [links],
    );

    const visibleLinks = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return validLinks;
        return validLinks.filter(
            (item) =>
                item.label.toLowerCase().includes(query) ||
                item.hostname.toLowerCase().includes(query) ||
                item.href.toLowerCase().includes(query),
        );
    }, [validLinks, searchQuery]);

    return (
        <div className={LINKS_SHELL}>
            <Dimmer active={loading} inverted>
                <Loader content="Loading links" />
            </Dimmer>

            <div className={LINKS_HERO}>
                <div className="links-hero-left min-w-0">
                    <div className="links-hero-eyebrow">Quick Access</div>
                    <div className="links-hero-title text-nbp-22 sm:text-nbp-24 md:text-nbp-28">
                        Community Links
                    </div>
                    <div className="links-hero-sub">Websites and resources, all in one place</div>
                </div>
                {validLinks.length > 0 && (
                    <div className={LINKS_HERO_SEARCH}>
                        <div className="links-hero-search-icon">
                            <SearchIcon size={17} />
                        </div>
                        <input
                            className="links-hero-search-input"
                            type="text"
                            aria-label="Search community links"
                            placeholder="Search links…"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                    </div>
                )}
            </div>

            {visibleLinks.length > 0 ? (
                <div className="links-section min-w-0 w-full">
                    <div className={LINKS_SECTION_HEADER}>
                        <div className="links-section-title">Current Links</div>
                        <div className="links-section-rule" />
                    </div>
                    <div className="links-grid min-w-0 w-full">
                        {visibleLinks.map((item) => (
                            <a
                                key={item.key}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={LINKS_CARD}
                            >
                                <div className="link-favicon h-nbp-40 w-nbp-40 sm:h-nbp-48 sm:w-nbp-48">
                                    <ChainLinkIcon />
                                </div>
                                <div className="link-body">
                                    <div className="link-name truncate" title={item.label}>{item.label}</div>
                                    {item.hasLabel && (
                                        <div className="link-desc truncate" title={item.hostname}>{item.hostname}</div>
                                    )}
                                </div>
                                <div className="link-arrow h-nbp-28 w-nbp-28 sm:h-nbp-32 sm:w-nbp-32">
                                    <ArrowIcon size={13} />
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            ) : (
                !loading && (
                    <div className={LINKS_EMPTY}>
                        <div className="links-empty-icon">
                            <ChainLinkIcon size={48} />
                        </div>
                        <div className="links-empty-title">No links found</div>
                        <div className="links-empty-sub">
                            {validLinks.length === 0
                                ? 'There are no community links to show yet.'
                                : 'Try a different search term.'}
                        </div>
                        {validLinks.length > 0 && (
                            <button
                                type="button"
                                className="links-empty-clear"
                                onClick={() => setSearchQuery('')}
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                )
            )}
        </div>
    );
};

export default LinksList;
